import type {CurriedSelector, Reducer, Selector} from '@/redux/types';
import type {State as AppState} from '@/redux/typesNew';
import type {Inventory} from '@/redux/v1/models';
import type {
  FetchInventoryError,
  FetchInventoryPending,
  FetchInventorySuccess,
} from '@/redux/v1/offers/inventory/actionTypes';
import type {State, StateByHotelId} from '@/redux/v1/offers/inventory/models';
import type {ActionHandlers} from '@travel-frontend/state-handling-sdk';

import {
  createReducerUsingActionHandlers,
  ERROR,
  PENDING,
  SUCCESS,
} from '@travel-frontend/state-handling-sdk';
import {curry} from 'lodash';
import {createSelector} from 'reselect';

import {FETCH_INVENTORY} from '@/redux/v1/offers/inventory/actionTypes';
import {inventoryState} from '@/redux/v1/offers/offersState';

export const initialState: State = {
  loading: {},
  byHotelId: {},
  errors: {},
  requests: {},
};

// @ts-expect-error ...
const ignoreStaleResponses = (actionHandler) => (state, action) =>
  action.meta.requestId === state.requests[action.meta.hotelId]?.id
    ? actionHandler(state, action)
    : state;

const actionCreators: ActionHandlers<State> = {
  [PENDING(FETCH_INVENTORY)]: (
    state: State,
    {meta}: FetchInventoryPending
  ) => ({
    ...state,
    loading: {
      ...state.loading,
      [meta.hotelId]: true,
    },
    requests: {
      ...state.requests,
      [meta.hotelId]: {
        id: meta.requestId,
      },
    },
  }),
  [SUCCESS(FETCH_INVENTORY)]: ignoreStaleResponses(
    (state: State, {data, meta}: FetchInventorySuccess) => ({
      ...state,
      loading: {
        ...state.loading,
        [meta.hotelId]: false,
      },
      byHotelId: {
        ...state.byHotelId,
        [meta.hotelId]: {
          unfiltered: data.unfiltered,
          filtered: data.filtered,
          translations: data.translations,
        },
      },
      errors: {
        ...state.errors,
        [meta.hotelId]: null,
      },
    })
  ),
  [ERROR(FETCH_INVENTORY)]: ignoreStaleResponses(
    (state: State, {error, meta}: FetchInventoryError) => ({
      ...state,
      loading: {
        ...state.loading,
        [meta.hotelId]: false,
      },
      errors: {
        ...state.errors,
        [meta.hotelId]: error,
      },
    })
  ),
};

export const inventoryReducer: Reducer<State> =
  createReducerUsingActionHandlers<State>(initialState, actionCreators);

export const getIsInventoryLoadedByHotelId = (): Selector<boolean> =>
  // @ts-expect-error ...
  createSelector(
    // @ts-expect-error ...
    [inventoryState, (_, hotelId) => hotelId],
    (state: State, hotelId: string) => state.byHotelId[hotelId] !== undefined
  );

export const getIsInventoryLoadingByHotelId = (): Selector<boolean> =>
  // @ts-expect-error ...
  createSelector(
    // @ts-expect-error ...
    [inventoryState, (_, hotelId) => hotelId],
    (state: State, hotelId: string) => state.loading[hotelId] === true
  );

export const getHasInventoryFailedLoadingByHotelId: CurriedSelector<
  string,
  boolean
> = curry((hotelId: string, state: AppState) =>
  Boolean(inventoryState(state).errors[hotelId])
);

const selectStateByHotelId: CurriedSelector<
  string,
  null | undefined | StateByHotelId
> = curry(
  // @ts-expect-error ...
  (hotelId, state: AppState) => inventoryState(state).byHotelId[hotelId]
);

export const selectInventoryUnfiltered: CurriedSelector<
  string,
  null | undefined | Inventory
> = curry(
  // @ts-expect-error ...
  (hotelId, state: AppState) => selectStateByHotelId(hotelId, state)?.unfiltered
);

export const selectInventoryFiltered: CurriedSelector<
  string,
  null | undefined | Inventory
> = curry(
  // @ts-expect-error ...
  (hotelId, state: AppState) => selectStateByHotelId(hotelId, state)?.filtered
);

export const selectTranslations: CurriedSelector<string, any> = curry(
  // @ts-expect-error ...
  (hotelId, state: AppState) =>
    selectStateByHotelId(hotelId, state)?.translations
);
